import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  InputAdornment,
  Autocomplete,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { vouchersService, productsService } from '../services/api';

const VoucherFormModal = ({ open, onClose, voucher, userId }) => {
  const isEditing = !!voucher;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);

  const [formData, setFormData] = useState({
    userId: userId,
    name: '',
    description: '',
    type: 'percentage',
    discountValue: '',
    freeProductId: null,
    minimumPurchase: '',
    maximumDiscount: '',
    validFrom: '',
    validUntil: '',
    reason: '',
    isCombinable: false,
  });

  useEffect(() => {
    if (open) {
      fetchProducts();
      if (voucher) {
        setFormData({
          userId: voucher.userId,
          name: voucher.name || '',
          description: voucher.description || '',
          type: voucher.type || 'percentage',
          discountValue: voucher.discountValue || '',
          freeProductId: voucher.freeProductId || null,
          minimumPurchase: voucher.minimumPurchase || '',
          maximumDiscount: voucher.maximumDiscount || '',
          validFrom: voucher.validFrom ? voucher.validFrom.slice(0, 16) : '',
          validUntil: voucher.validUntil ? voucher.validUntil.slice(0, 16) : '',
          reason: voucher.reason || '',
          isCombinable: voucher.isCombinable || false,
        });
      } else {
        setFormData({
          userId: userId,
          name: '',
          description: '',
          type: 'percentage',
          discountValue: '',
          freeProductId: null,
          minimumPurchase: '',
          maximumDiscount: '',
          validFrom: '',
          validUntil: '',
          reason: '',
          isCombinable: false,
        });
      }
    }
  }, [open, voucher, userId]);

  const fetchProducts = async () => {
    try {
      const response = await productsService.getAll();
      setProducts(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des produits:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const dataToSend = {
        ...formData,
        discountValue: formData.discountValue ? parseFloat(formData.discountValue) : null,
        minimumPurchase: formData.minimumPurchase ? parseFloat(formData.minimumPurchase) : null,
        maximumDiscount: formData.maximumDiscount ? parseFloat(formData.maximumDiscount) : null,
      };

      if (isEditing) {
        await vouchersService.update(voucher.id, dataToSend);
      } else {
        await vouchersService.create(dataToSend);
      }

      onClose(true); // true pour indiquer qu'il faut rafraîchir
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde du bon');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditing ? 'Modifier le bon' : 'Créer un nouveau bon'}
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Nom du bon"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Type de réduction</InputLabel>
                <Select
                  value={formData.type}
                  label="Type de réduction"
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <MenuItem value="percentage">Pourcentage</MenuItem>
                  <MenuItem value="fixed">Montant fixe</MenuItem>
                  <MenuItem value="free_product">Produit offert</MenuItem>
                  <MenuItem value="free_shipping">Livraison gratuite</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.type === 'percentage' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Pourcentage"
                  value={formData.discountValue}
                  onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                />
              </Grid>
            )}

            {formData.type === 'fixed' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Montant"
                  value={formData.discountValue}
                  onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
            )}

            {formData.type === 'free_product' && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={products}
                  getOptionLabel={(option) => option.name || ''}
                  value={products.find((p) => p.id === formData.freeProductId) || null}
                  onChange={(e, newValue) => {
                    setFormData({ ...formData, freeProductId: newValue?.id || null });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Produit offert"
                      required
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Montant minimum d'achat"
                value={formData.minimumPurchase}
                onChange={(e) => setFormData({ ...formData, minimumPurchase: e.target.value })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            {formData.type === 'percentage' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Réduction maximale"
                  value={formData.maximumDiscount}
                  onChange={(e) => setFormData({ ...formData, maximumDiscount: e.target.value })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Plafond de la réduction"
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Valide à partir de"
                value={formData.validFrom}
                onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Valide jusqu'au"
                value={formData.validUntil}
                onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Raison de l'attribution"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="Ex: Geste commercial, Cadeau de bienvenue..."
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isCombinable}
                    onChange={(e) => setFormData({ ...formData, isCombinable: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      📎 Bon combinable avec d'autres réductions
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Si activé, ce bon pourra être utilisé en même temps que d'autres bons combinables
                    </Typography>
                  </Box>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => onClose(false)}>Annuler</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : isEditing ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default VoucherFormModal;



